/** Brand colors matched to Ooudh Kraft logo metallic gold */
export const BRAND_GOLD = {
  highlight: '#F9E076',
  primary: '#D4AF37',
  mid: '#C5A059',
  shadow: '#7A5C2E',
  black: '#1A1408',
  white: '#FFFFFF',
  ivory: '#FAF8F4',
  cream: '#F3EFE8',
  parchment: '#EBE6DE',
} as const

/** Metallic gradient stops — logo wordmark foil */
export const BRAND_GOLD_GRADIENT =
  'linear-gradient(90deg, #7A5C2E 0%, #C5A059 22%, #F9E076 45%, #D4AF37 68%, #C5A059 88%, #7A5C2E 100%)'

export const BRAND_BUTTON_GRADIENT =
  'linear-gradient(135deg, #9A7D32 0%, #C5A059 35%, #D4AF37 65%, #B8943F 100%)'

export const CHART_GOLD_COLORS = [
  BRAND_GOLD.primary,
  BRAND_GOLD.mid,
  BRAND_GOLD.highlight,
  BRAND_GOLD.shadow,
  '#9A7D32',
]

export const STORE_NAME = 'Ooudh Kraft'
export const STORE_NAME_SLUG = 'ooud-kraft'
export const STORE_TAGLINE = 'Fragrance that inspires elegance'
export const DEFAULT_STORE_URL = 'https://www.ooudhkraft.com'
export const DEFAULT_CONTACT_EMAIL = 'contact@ooudkraft.com'
export const DEFAULT_ORDER_SUPPORT_EMAIL = 'support@ooudkraft.com'
export const DEFAULT_INSTAGRAM_URL = 'https://instagram.com/ooudkraft'
export const DEFAULT_INSTAGRAM_HANDLE = '@ooudkraft'

/** Full brand lockup (icon + wordmark + tagline). */
export const LOGO_PATH = '/logo.png'
/** Horizontal lockup for storefront top bar (mobile, tablet & desktop). */
export const LOGO_MOBILE_PATH = '/logo-mobile.jpg'
/** Square emblem for favicons, avatars, and compact UI. */
export const LOGO_ICON_PATH = '/logo-icon.png'
export const FAVICON_16_PATH = '/favicon-16x16.png'
export const FAVICON_32_PATH = '/favicon-32x32.png'
export const APPLE_TOUCH_ICON_PATH = '/apple-touch-icon.png'
export const LOGO_ALT = `${STORE_NAME} — ${STORE_TAGLINE}`

/** Homepage hero banner image specs (matches admin preview & storefront crop). */
export const BANNER_IMAGE_WIDTH = 1920
/** Base ratio plus fixed extra height on storefront — sync with `.store-banner-media` in index.css. */
export const BANNER_IMAGE_ASPECT_WIDTH = 21
export const BANNER_IMAGE_ASPECT_HEIGHT = 10
export const BANNER_IMAGE_STOREFRONT_EXTRA_HEIGHT = 30
export const BANNER_IMAGE_HEIGHT = Math.round(
  (BANNER_IMAGE_WIDTH * BANNER_IMAGE_ASPECT_HEIGHT) /
    BANNER_IMAGE_ASPECT_WIDTH +
    BANNER_IMAGE_STOREFRONT_EXTRA_HEIGHT
)
export const BANNER_IMAGE_ASPECT = `${BANNER_IMAGE_ASPECT_WIDTH}:${BANNER_IMAGE_ASPECT_HEIGHT}`
export const BANNER_IMAGE_ASPECT_RATIO = `${BANNER_IMAGE_WIDTH} / ${BANNER_IMAGE_HEIGHT}`
export const BANNER_IMAGE_DIMENSIONS = `${BANNER_IMAGE_WIDTH} × ${BANNER_IMAGE_HEIGHT} px`
export const BANNER_IMAGE_FORMATS = 'JPG, PNG, or WEBP'

/** Homepage category icons — circular crop on storefront (`HomeCategories.tsx`). */
export const CATEGORY_STOREFRONT_SIZE_MOBILE = 120
export const CATEGORY_STOREFRONT_SIZE_DESKTOP = 142
export const CATEGORY_IMAGE_DISPLAY_SIZE = `${CATEGORY_STOREFRONT_SIZE_MOBILE}–${CATEGORY_STOREFRONT_SIZE_DESKTOP} px`
/** Recommended upload — 2× largest storefront size for retina. */
export const CATEGORY_IMAGE_WIDTH = CATEGORY_STOREFRONT_SIZE_DESKTOP * 2
export const CATEGORY_IMAGE_HEIGHT = CATEGORY_IMAGE_WIDTH
export const CATEGORY_IMAGE_ASPECT = '1:1'
export const CATEGORY_IMAGE_DIMENSIONS = `${CATEGORY_IMAGE_WIDTH} × ${CATEGORY_IMAGE_HEIGHT} px`
export const CATEGORY_IMAGE_FORMATS = 'JPG, PNG, or WEBP'
/** Upload max edge — matches `optimizeImageForUpload` category preset. */
export const CATEGORY_IMAGE_MAX_EDGE = 400

/** Fragrance note icons — homepage & `/notes` (`FragranceNoteChip.tsx`). */
export const NOTE_STOREFRONT_SIZE_MOBILE = 160
export const NOTE_STOREFRONT_SIZE_TABLET = 164
export const NOTE_STOREFRONT_SIZE_DESKTOP = 180
export const NOTE_IMAGE_DISPLAY_SIZE = `${NOTE_STOREFRONT_SIZE_MOBILE}–${NOTE_STOREFRONT_SIZE_DESKTOP} px`
/** Recommended upload — 2× largest storefront size for retina. */
export const NOTE_IMAGE_WIDTH = NOTE_STOREFRONT_SIZE_DESKTOP * 2
export const NOTE_IMAGE_HEIGHT = NOTE_IMAGE_WIDTH
export const NOTE_IMAGE_ASPECT = '1:1'
export const NOTE_IMAGE_DIMENSIONS = `${NOTE_IMAGE_WIDTH} × ${NOTE_IMAGE_HEIGHT} px`
export const NOTE_IMAGE_FORMATS = 'JPG, PNG, or WEBP'
/** Upload max edge — matches `optimizeImageForUpload` note preset. */
export const NOTE_IMAGE_MAX_EDGE = 640

/** Featured video reel card thumbnail (9:16 portrait, behind play overlay). */
export const REEL_THUMBNAIL_WIDTH = 720
export const REEL_THUMBNAIL_HEIGHT = 1280
export const REEL_THUMBNAIL_ASPECT = '9:16'
export const REEL_THUMBNAIL_DIMENSIONS = `${REEL_THUMBNAIL_WIDTH} × ${REEL_THUMBNAIL_HEIGHT} px`
export const REEL_THUMBNAIL_FORMATS = 'JPG, PNG, or WEBP'
/** Storefront card is ~280–300px wide; uploads compressed to this max edge. */
export const REEL_THUMBNAIL_MAX_EDGE = 640
export const REEL_THUMBNAIL_DISPLAY_SIZE = '280 × 498 px'

/** Product & variant images — square crop on cards; detail gallery uses contain. */
export const PRODUCT_IMAGE_WIDTH = 1200
export const PRODUCT_IMAGE_HEIGHT = 1200
export const PRODUCT_IMAGE_ASPECT = '1:1'
export const PRODUCT_IMAGE_DIMENSIONS = `${PRODUCT_IMAGE_WIDTH} × ${PRODUCT_IMAGE_HEIGHT} px`
export const PRODUCT_IMAGE_FORMATS = 'JPG, PNG, or WEBP'
/** Matches `optimizeImageForUpload` product preset max edge. */
export const PRODUCT_IMAGE_MAX_EDGE = 1200
export const PRODUCT_IMAGE_MAX_COUNT = 5

/** Founder's Desk portrait on the homepage. */
export const FOUNDER_IMAGE_WIDTH = 800
export const FOUNDER_IMAGE_HEIGHT = 1000
export const FOUNDER_IMAGE_ASPECT = '4:5'
export const FOUNDER_IMAGE_DIMENSIONS = `${FOUNDER_IMAGE_WIDTH} × ${FOUNDER_IMAGE_HEIGHT} px`
export const FOUNDER_IMAGE_FORMATS = 'JPG, PNG, or WEBP'
export const FOUNDER_IMAGE_MAX_EDGE = 800
export const FOUNDER_IMAGE_DISPLAY_SIZE = '~320 × 400 px'
/** Default founder portrait for the homepage Founder's Desk section. */
export const FOUNDER_PORTRAIT_PATH = '/founder-portrait.png'
/** Placeholder portrait when no custom image is uploaded in settings. */
export const DEFAULT_FOUNDER_IMAGE = FOUNDER_PORTRAIT_PATH
